/**
 * @file image-with-fallback.tsx
 * @module app/modules/_common/components/image-with-fallback
 * @description Reusable `<img>` wrapper for UTrade Admin that falls back to a default image
 * when `src` is empty/undefined or fails to load (broken link, 404, network error). If the
 * fallback also fails, the image stops there, which prevents an infinite error loop.
 * `fallbackSrc` defaults to the app-wide placeholder image, so most callers only need to
 * import this component (not a separate default-image asset); pass `fallbackSrc` to override.
 * An image with no size of its own gets a default 50x50; pass `noDefaultSize` when CSS or the
 * image's natural size should decide instead.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.1
 * @version 2.1.2
 */

import React from 'react';
import appDefaultImage from '../../../../_admin/assets/media/product/default.svg';

interface ImageWithFallbackProps
  extends React.ImgHTMLAttributes<HTMLImageElement> {
  src?: string;
  fallbackSrc?: string;
  /** Skips the default 50x50 size, for images sized by CSS or by their natural size. */
  noDefaultSize?: boolean;
}

const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({
  src,
  fallbackSrc = appDefaultImage,
  alt = '',
  noDefaultSize = false,
  width = noDefaultSize ? undefined : 50,
  height = noDefaultSize ? undefined : 50,
  onError,
  ...rest
}) => {
  return (
    <img
      src={src || fallbackSrc}
      alt={alt}
      width={width}
      height={height}
      onError={(e) => {
        const img = e.target as HTMLImageElement;
        // If the fallback itself failed, stop here; setting src again would retry forever.
        if (img.getAttribute('src') !== fallbackSrc) {
          img.src = fallbackSrc;
        }
        onError?.(e);
      }}
      {...rest}
    />
  );
};

export default ImageWithFallback;
